import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart2,
  Bot,
  Check,
  ChevronDown,
  Clock,
  Cloud,
  Layers,
  Loader2,
  Plus,
  Shield,
  Trophy,
  Users,
} from "lucide-react";
import { formatFixtureDate, formatFixtureTime } from "../../utils/formatFixtureDate";
import { isFixtureLive } from "../../utils/p2pCreateHelpers";
import { formatKickoffCountdown } from "../../utils/fixturesDeskHelpers";
import { goToFixturePredictions } from "../../utils/predictionsNavigation";

/* ─── form dots ────────────────────────────────────────── */
const FormDots = ({ form }) => {
  const chars = (form || "").split("").slice(0, 5);
  if (!chars.length) return null;
  return (
    <div className="afx-form">
      {chars.map((c, i) => (
        <span
          key={i}
          className={`afx-form-dot afx-form-${c.toLowerCase()}`}
          title={c === "W" ? "Win" : c === "D" ? "Draw" : "Loss"}
        />
      ))}
    </div>
  );
};

/* ─── team crest ───────────────────────────────────────── */
const Crest = ({ team }) => (
  <span className="afx-row-crest">
    {team?.logo ? <img src={team.logo} alt="" /> : <Shield size={16} aria-hidden />}
  </span>
);

/* ─── prediction chips ─────────────────────────────────── */
const PRED_CHIPS = [
  { key: "community", label: "Community", Icon: Users, tone: "community" },
  { key: "ai", label: "AI picks", Icon: Bot, tone: "ai" },
  { key: "total", label: "Total", Icon: Layers, tone: "total" },
];
const predValue = (counts, key) => {
  if (key === "community") return counts?.communityPredictions ?? 0;
  if (key === "ai") return counts?.aiPredictions ?? 0;
  return counts?.totalPredictions ?? 0;
};

/* ─── main component ───────────────────────────────────── */
const AddFixtureCatalogRow = ({
  row,
  hideLeague = false,
  onAdd,
  busy = false,
  added = false,
  stamped = false,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const { fixture, league } = row;
  const live = isFixtureLive(fixture);
  const home = fixture?.homeTeam || {};
  const away = fixture?.awayTeam || {};
  const preds = fixture?.predictionCounts;
  const standings = fixture?.standings;
  const hasStandings = Boolean(standings?.home || standings?.away);
  const hasPreds = Boolean(preds);
  const panelId = `afx-panel-${fixture?._id || fixture?.id}`;
  const totalPreds = predValue(preds, "total");
  const countdown = live ? null : formatKickoffCountdown(fixture);

  const goPredictions = (mode) => {
    goToFixturePredictions(navigate, { homeTeam: home, awayTeam: away, mode });
  };

  const rowClass = [
    "afx-row",
    expanded ? "afx-row-open" : "",
    added ? "afx-row-added" : "",
    live ? "afx-row-live" : "",
    stamped ? "afx-row-stamp" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={rowClass}>
      <div className="afx-row-main">
        <div className="afx-row-time">
          {live ? (
            <span className="afx-live-pill">
              <span className="afx-live-dot" />
              LIVE
            </span>
          ) : (
            <>
              <time className="afx-row-clock" dateTime={fixture?.date}>
                {formatFixtureTime(fixture?.date)}
              </time>
              <span className="afx-row-date">{formatFixtureDate(fixture?.date)}</span>
            </>
          )}
        </div>

        <div className="afx-row-match">
          <span className="afx-row-team afx-row-team-home">
            <Crest team={home} />
            <span className="afx-row-team-name" title={home.name}>
              {home.name || "Home"}
            </span>
            <FormDots form={standings?.home?.form} />
          </span>

          <span className="afx-row-versus">vs</span>

          <span className="afx-row-team afx-row-team-away">
            <Crest team={away} />
            <span className="afx-row-team-name" title={away.name}>
              {away.name || "Away"}
            </span>
            <FormDots form={standings?.away?.form} />
          </span>
        </div>

        <div className="afx-row-meta">
          {!hideLeague && league?.name ? (
            <span className="afx-row-league" title={league.name}>
              {league.logo ? (
                <img src={league.logo} alt="" />
              ) : (
                <Trophy size={11} aria-hidden />
              )}
              {league.name}
            </span>
          ) : null}
          {fixture?.hasExternalApiOdds ? (
            <span className="afx-chip-feed">
              <Cloud size={9} aria-hidden />
              Feed
            </span>
          ) : null}
          {!live && countdown ? (
            <span className={`afx-chip-countdown${countdown.urgent ? " afx-chip-urgent" : ""}`}>
              <Clock size={9} aria-hidden />
              {countdown.text}
            </span>
          ) : null}
          {totalPreds > 0 ? (
            <button type="button" className="afx-signal" onClick={() => goPredictions()}>
              <BarChart2 size={10} aria-hidden />
              {totalPreds}
            </button>
          ) : null}
          {hasStandings ? <span className="afx-signal afx-signal-static">Table</span> : null}
          {!totalPreds && !hasStandings ? (
            <span className="afx-signal afx-signal-static">Fresh</span>
          ) : null}
        </div>

        <div className="afx-row-actions">
          {added ? (
            <span className="afx-row-seal">
              <Check size={11} strokeWidth={3} aria-hidden />
              Boarded
            </span>
          ) : null}

          <button
            type="button"
            className={`afx-details-btn${expanded ? " afx-details-btn-open" : ""}`}
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={() => setExpanded((v) => !v)}
          >
            Intel
            <ChevronDown size={13} aria-hidden />
          </button>

          <button
            type="button"
            className={`afx-add-btn${added ? " afx-add-btn-done" : ""}`}
            disabled={disabled || busy || added}
            aria-label={added ? "Fixture added to house board" : "Add fixture to house board"}
            onClick={() => !disabled && !busy && !added && onAdd?.({ fixture, league })}
          >
            {busy ? (
              <Loader2 size={14} className="afx-spin" />
            ) : added ? (
              <>
                <Check size={14} strokeWidth={2.6} />
                On board
              </>
            ) : (
              <>
                <Plus size={14} strokeWidth={2.6} />
                Add
              </>
            )}
          </button>
        </div>
      </div>

      <div id={panelId} className={`afx-panel${expanded ? " afx-panel-open" : ""}`} aria-hidden={!expanded}>
        <div className="afx-panel-inner">
          <section className="afx-panel-block">
            <div className="afx-panel-head">
              <p className="afx-panel-label">Predictions</p>
              {hasPreds ? (
                <button type="button" className="afx-panel-link" onClick={() => goPredictions()}>
                  Open Tips
                </button>
              ) : null}
            </div>
            {hasPreds ? (
              <div className="afx-pred-strip">
                {PRED_CHIPS.map(({ key, label, Icon, tone }) => {
                  const val = predValue(preds, key);
                  const mode = key === "ai" ? "ai" : key === "community" ? "community" : undefined;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`afx-pred-chip afx-pred-${tone}${val > 0 ? " afx-pred-chip-active" : ""}`}
                      onClick={() => goPredictions(mode)}
                      title={`View ${label.toLowerCase()} on Tips`}
                    >
                      <span>
                        <Icon size={9} aria-hidden />
                        {label}
                      </span>
                      <strong className="tabular-nums">{val}</strong>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="afx-panel-empty">No predictions yet</p>
            )}
          </section>

          {hasStandings ? (
            <section className="afx-panel-block">
              <div className="afx-panel-head">
                <p className="afx-panel-label">League table</p>
              </div>
              <div className="afx-stand">
                {[
                  { side: standings.home, team: home },
                  { side: standings.away, team: away },
                ].map(({ side, team }, i) => (
                  <div key={i} className="afx-stand-line">
                    <div className="afx-stand-team">
                      {team?.logo ? (
                        <img src={team.logo} alt="" />
                      ) : (
                        <Shield size={11} aria-hidden />
                      )}
                      <span className="afx-stand-name">{team?.name || "—"}</span>
                    </div>
                    {side ? (
                      <>
                        <div className="afx-stand-meta">
                          <span className="afx-stand-stat">
                            <em>Rank</em>
                            <strong>{side.rank != null ? `#${side.rank}` : "—"}</strong>
                          </span>
                          <span className="afx-stand-stat">
                            <em>Pts</em>
                            <strong className="tabular-nums">{side.points ?? "—"}</strong>
                          </span>
                          <span className="afx-stand-stat">
                            <em>Pl</em>
                            <strong className="tabular-nums">{side.played ?? "—"}</strong>
                          </span>
                        </div>
                        <FormDots form={side.form} />
                      </>
                    ) : (
                      <p className="afx-panel-empty">No data</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export default AddFixtureCatalogRow;
